import { HttpClient, robotoHeaders } from "@/shared/http";

import type {
  AddFeatureFlagRolloutGroupsRequest,
  CreateFeatureFlagRequest,
  FeatureFlagRecord,
  RemoveFeatureFlagRolloutGroupsRequest,
  UpdateFeatureFlagRequest,
} from "./FeatureFlagRecord";

/** Root path for the feature-flags REST API. */
const BASE_PATH = "v1/feature-flags";

/** Build a path that addresses a flag by its server-assigned ID, with an optional sub-resource suffix (e.g. "/restore"). */
const byId = (featureFlagId: string, suffix = ""): string =>
  `${BASE_PATH}/id/${featureFlagId}${suffix}`;

/** Build a path that addresses a flag by its human-readable name, with an optional sub-resource suffix (e.g. "/check"). The name is URL-encoded so callers can pass it raw. */
const byName = (name: string, suffix = ""): string =>
  `${BASE_PATH}/name/${encodeURIComponent(name)}${suffix}`;

/**
 * REST client for the feature-flags API.
 *
 * Methods fall into two groups:
 *   - Admin management (getAll, create, update, delete, getAllDeleted, restore,
 *     and the rollout-group mutators) — require Roboto-admin authorization
 *     server-side and are used by the admin UI.
 *   - Caller-facing checks (checkAllFeatureFlags, checkFeatureFlag) — used by
 *     any signed-in user to discover which flags are enabled for their
 *     (user, org) pair.
 *
 * Every method rejects if the underlying fetch fails (network error, non-2xx
 * status, or aborted signal); callers handle errors via TanStack Query.
 */
export class FeatureFlagService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * List every active (non-deleted) feature flag. Used by the admin UI to
   * populate the flag table. Pass `abortSignal` to cancel an in-flight
   * request when the caller unmounts or re-queries.
   */
  public async getAll(
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<FeatureFlagRecord[]> {
    const url = this.#httpClient.constructUrl(BASE_PATH);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<FeatureFlagRecord[]>();
  }

  /**
   * Create a new feature flag and return the persisted record. Invoked when an
   * admin submits the "new flag" form. Rejects if a flag with the same name
   * already exists (server returns 4xx).
   */
  public async create(
    request: CreateFeatureFlagRequest,
  ): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(BASE_PATH);
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
    });
    return response.json<FeatureFlagRecord>();
  }

  /**
   * Apply a partial update to an existing flag (name, description, or rollout
   * status) and return the refreshed record. Invoked when an admin edits a
   * flag. To change the set of rollout user groups, prefer
   * `addRolloutGroups` / `removeRolloutGroups`.
   */
  public async update(
    featureFlagId: string,
    request: UpdateFeatureFlagRequest,
  ): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(byId(featureFlagId));
    const response = await this.#httpClient.put(url, {
      body: JSON.stringify(request),
    });
    return response.json<FeatureFlagRecord>();
  }

  /**
   * Soft-delete a flag. The server marks the row as deleted but retains it so
   * an admin can recover it via `restore`. Resolves with no value on success
   * (HTTP 204) and rejects on any non-2xx status.
   */
  public async delete(featureFlagId: string): Promise<void> {
    const url = this.#httpClient.constructUrl(byId(featureFlagId));
    const response = await this.#httpClient.delete(url);
    return response.throwIfError();
  }

  /**
   * List flags that have been soft-deleted. Used by the admin UI's "deleted
   * flags" view to offer restoration. Pass `abortSignal` to cancel an
   * in-flight request.
   */
  public async getAllDeleted(
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<FeatureFlagRecord[]> {
    const url = this.#httpClient.constructUrl(`${BASE_PATH}/deleted`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<FeatureFlagRecord[]>();
  }

  /**
   * Undo a soft-delete and return the restored record. Invoked when an admin
   * clicks "restore" on a previously deleted flag.
   */
  public async restore(featureFlagId: string): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(byId(featureFlagId, "/restore"));
    const response = await this.#httpClient.post(url, {});
    return response.json<FeatureFlagRecord>();
  }

  /**
   * Return a `{ orgId: { flagName: enabled } }` matrix covering every flag,
   * evaluated for the calling user within each org they belong to.
   *
   * Fetched once per session to seed the feature-flag cache; callers index the
   * result by whichever org is active, so switching orgs needs no new request.
   * No org header is sent — the server derives the org set from the caller's
   * memberships.
   */
  public async checkAllFeatureFlags(
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<Record<string, Record<string, boolean>>> {
    const url = this.#httpClient.constructUrl(`${BASE_PATH}/check/by-org`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<Record<string, Record<string, boolean>>>();
  }

  /**
   * Return whether the named flag is enabled for the calling user inside
   * `orgId`. Used for ad-hoc checks of a single flag; the response shape
   * `{ enabled: boolean }` is unwrapped before returning.
   */
  public async checkFeatureFlag(
    name: string,
    orgId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<boolean> {
    const url = this.#httpClient.constructUrl(byName(name, "/check"));
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    const result = await response.json<{ enabled: boolean }>();
    return result.enabled;
  }

  /**
   * Associate one or more user groups with the flag's rollout-group set, so
   * members of those groups see the flag as enabled. The server treats this
   * as idempotent — re-adding an already-associated group is a no-op rather
   * than an error. Returns the refreshed flag record.
   */
  public async addRolloutGroups(
    featureFlagId: string,
    request: AddFeatureFlagRolloutGroupsRequest,
  ): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(
      byId(featureFlagId, "/rollout-groups"),
    );
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
    });
    return response.json<FeatureFlagRecord>();
  }

  /**
   * Disassociate one or more user groups from the flag's rollout-group set.
   * Idempotent: removing a group that is not currently associated is a no-op
   * rather than an error. Returns the refreshed flag record.
   */
  public async removeRolloutGroups(
    featureFlagId: string,
    request: RemoveFeatureFlagRolloutGroupsRequest,
  ): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(
      byId(featureFlagId, "/rollout-groups"),
    );
    const response = await this.#httpClient.delete(url, {
      body: JSON.stringify(request),
    });
    return response.json<FeatureFlagRecord>();
  }

  /**
   * Return the IDs of user groups currently associated with the flag's
   * rollout-group set. Used by the admin UI to populate the rollout-groups
   * editor when an admin opens a flag's detail view.
   */
  public async listRolloutGroups(
    featureFlagId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<string[]> {
    const url = this.#httpClient.constructUrl(
      byId(featureFlagId, "/rollout-groups"),
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<string[]>();
  }
}
